import { Box, Button, Checkbox, Chip, Divider, FormControlLabel, Paper, TextField, Typography, useMediaQuery } from "@mui/material";
import { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { useModals } from "../../../context/ModalContext";
import { useGlobalSnackbar } from "../../../context/SnackbarContext";
import { AccessGate, OverlayBehavior, useBenefitStrategy } from "../../../permissions/access";
import { BenefitPreviewGate } from "../../../permissions/benefits/components/gates/BenefitPreviewGate";
import { EBenefitTypes } from "../../../permissions/benefits/enums/benefits.enums";
import { useBenefit } from "../../../permissions/benefits/hooks/useBenefits";
import { quoteCreateActions } from "../../../redux/features/quotes/quote-create-advanced";
import { selectClientSelection } from "../../../redux/features/quotes/quote-create-advanced/selectors/clientSelectors";
import { selectQuoteCreatePayload } from "../../../redux/features/quotes/quote-create-advanced/selectors/quoteCreateSelectors";
import { clearQuotes } from "../../../redux/features/quotes/quoteListSlice";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks/redux";
import { decodeToken } from "../../../utils";

import { usePaymentMethodState, useSetPaymentMethod } from "../../invoice/create/PaymentMethods/states/usePaymentMethod";
import { PaymentMethodState } from "../../invoice/create/PaymentMethods/types";
import { GenericProcessLoader } from "../../shared/components/loaders/GenericProcessLoader";
import { colors } from "../../ui/colors";

import { QuoteAdvancedList } from "./advanced-quote/QuoteAdvancedList";
import { QuoteBasicList } from "./basic-quote/QuoteBasicList";
import { ClientInfo } from "./basic-quote/components/ClientInfo";
import { CompanyInfo } from "./basic-quote/components/CompanyInfo";
import { textFieldStyles } from "./basic-quote/components/QuoteBasicRow";
import { useQuoteTotal } from "./basic-quote/hooks/useQuoteTotal";
import { QuoteModeEnum } from "./enums/quote-create.enums";
import { buildQuotesAdvanced } from "./helper/buildQuoteAdvanced";
import { buildQuotePayload } from "./helper/buildQuotePayload";
import {
  advancedQuotesHaveCostOverrideDefined,
  basicQuotesHaveLinePriceDefined,
  isDepositRequirementSatisfied,
} from "./helper/quoteServicePricingGate";
import { useGenerateQuoteMutation } from "./hooks/useGenerateQuoteMutation";
import { QuoteDeposit } from "./QuoteDeposit";
import { quoteModern } from "../quote-detail/summary/style/style";
import { WorkOutline, WorkOutlineRounded } from "@mui/icons-material";

export const QuoteCreation = () => {
  const isXs = useMediaQuery("(max-width:600px)");

  const [mode, setMode] = useState<QuoteModeEnum>(QuoteModeEnum.BASIC);
  const [showLoading, setShowLoading] = useState(false);

  const quoteContainerRef = useRef<HTMLDivElement>(null);

  const { showSnackbar } = useGlobalSnackbar();
  const { closeByKey, openModal } = useModals();

  const dispatch = useAppDispatch();

  const paymentMethodState: PaymentMethodState = usePaymentMethodState();

  const { reset: resetPaymentMethodStore } = useSetPaymentMethod();

  const { canUseNow: hasAdvancedQuoteBenefit } = useBenefit(EBenefitTypes.QUOTES_ADVANCED);

  const isPreviewingAdvanced = mode === QuoteModeEnum.ADVANCED && !hasAdvancedQuoteBenefit;

  const { uid: uidFromState } = useAppSelector((state) => state.userState);

  const clientSelection = useSelector(selectClientSelection);

  const quoteSelection = useSelector(selectQuoteCreatePayload);

  const basicQuoteList = useAppSelector((state) => state.quoteList.basicQuotesList);

  const advancedQuoteList = useAppSelector((state) => state.quoteList.advancedQuoteslist);

  const businessUid = useMemo((): string => {
    if (uidFromState) {
      return uidFromState;
    }

    const token = sessionStorage.getItem("token");

    if (!token) {
      return "";
    }

    const decoded = decodeToken(token) as {
      uid?: string;
    } | null;

    return decoded?.uid ?? "";
  }, [uidFromState]);

  const hasServicePricing =
    mode === QuoteModeEnum.BASIC ? basicQuotesHaveLinePriceDefined(basicQuoteList) : advancedQuotesHaveCostOverrideDefined(advancedQuoteList);

  const depositRequired = Boolean(quoteSelection?.quote?.depositRequired);

  const depositOk = isDepositRequirementSatisfied(quoteSelection?.quote);

  const hasSelectedPaymentMethod = (paymentMethodState?.selectedPayments?.length ?? 0) > 0;

  const isMarkedAsPaid = Boolean(paymentMethodState?.markAsPaid);

  const paymentMethodOk = !depositRequired || hasSelectedPaymentMethod || isMarkedAsPaid;

  const isValidPayload =
    (basicQuoteList.length > 0 || advancedQuoteList.length > 0) && Boolean(clientSelection) && hasServicePricing && depositOk && paymentMethodOk;

  const { clearClientSelection, setQuote, clearQuote } = quoteCreateActions;

  const total = useQuoteTotal(mode);

  const {
    mutate: generateQuote,
    isSuccess,
    reset,
  } = useGenerateQuoteMutation({
    onError: () => {
      setShowLoading(false);
      reset();

      showSnackbar("Error generating quote", "error", 4000, "bottom-center");
    },

    onSuccess: () => {
      dispatch(clearQuotes());
      dispatch(clearQuote(undefined));
      dispatch(clearClientSelection(undefined));

      resetPaymentMethodStore();
    },
  });

  const handleLoaderFinish = useCallback(() => {
    setShowLoading(false);
    reset();

    showSnackbar("Quote created successfully! It is now pending acceptance by the client.", "success", 5000, "top-center");

    void closeByKey("Dashboard-QuoteAdd-Business");
    void closeByKey("Dashboard-QuotesAdd-Business");

    window.setTimeout(() => {
      openModal("Dashboard-Quotes-Business");
    }, 0);
  }, [closeByKey, openModal, reset, showSnackbar]);

  const handleGenerateQuote = () => {
    if (mode === QuoteModeEnum.BASIC && !basicQuotesHaveLinePriceDefined(basicQuoteList)) {
      showSnackbar("Define a price greater than zero for each basic service line before generating the quote.", "warning", 5000, "top-center");

      return;
    }

    if (mode === QuoteModeEnum.ADVANCED && !advancedQuotesHaveCostOverrideDefined(advancedQuoteList)) {
      showSnackbar(
        "Define a cost override price greater than zero for each advanced service (summary step) before generating the quote.",
        "warning",
        5000,
        "top-center",
      );

      return;
    }

    if (!isDepositRequirementSatisfied(quoteSelection?.quote)) {
      showSnackbar(
        "Require deposit is enabled: enter a deposit amount or percentage greater than zero before generating the quote.",
        "warning",
        5000,
        "top-center",
      );

      return;
    }

    if (!paymentMethodOk) {
      showSnackbar("Select at least one payment method or mark the quote as paid before generating it.", "warning", 5000, "top-center");

      return;
    }

    const advancedQuoteListPayload = buildQuotesAdvanced(advancedQuoteList);

    const payload = buildQuotePayload({
      mode,
      basicQuotes: basicQuoteList,
      advancedQuotes: advancedQuoteListPayload,
      clientSelection,
      alias: quoteSelection?.quote?.alias,
      requiredSign: quoteSelection?.quote?.requiredSign,
      depositAmount: quoteSelection?.quote?.depositAmount,
      depositPercent: quoteSelection?.quote?.depositPercent,
      paymentMethod: paymentMethodState,
      depositRequired,
    });

    setShowLoading(true);

    requestAnimationFrame(() => {
      quoteContainerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });

    generateQuote({
      payload,
    });
  };

  return (
    <Box
      ref={quoteContainerRef}
      sx={{
        position: "relative",
        p: 1,
        minHeight: "80vh",
        mx: isXs ? 0 : 2,
      }}
    >
      {showLoading && (
        <Box
          role="status"
          aria-live="polite"
          aria-label="Generating quote"
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 100,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            pt: {
              xs: 4,
              sm: 8,
            },
            px: 2,
            bgcolor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.82)",
            backdropFilter: "blur(4px)",
          }}
        >
          <GenericProcessLoader
            isPending={showLoading}
            isSuccess={isSuccess}
            onFinish={handleLoaderFinish}
            status={{
              first: "Calculating Quote",
              second: "Saving quote",
              third: "Sending to client",
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 2,
          mt: 2,
        }}
      >
        {mode === QuoteModeEnum.BASIC ? (
          <BenefitPreviewGate benefit={EBenefitTypes.QUOTES_ADVANCED} featureName="Advanced Quotes" onPreview={() => setMode(QuoteModeEnum.ADVANCED)}>
            <Button
              variant="contained"
              sx={{
                borderRadius: 2,
                textTransform: "none",
              }}
              onClick={() => {
                dispatch(clearQuotes());
                dispatch(clearQuote(undefined));
                setMode(QuoteModeEnum.ADVANCED);
              }}
            >
              Go to Advanced
            </Button>
          </BenefitPreviewGate>
        ) : (
          <Button
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: "none",
            }}
            onClick={() => {
              dispatch(clearQuotes());
              dispatch(clearQuote(undefined));
              setMode(QuoteModeEnum.BASIC);
            }}
          >
            Back to Basic
          </Button>
        )}
      </Box>

      <Typography
        variant="h4"
        fontWeight={900}
        textAlign="center"
        mb={4}
        mt={2}
        sx={{
          color: colors.orange.base,
          letterSpacing: 1,
        }}
      >
        ESTIMATE
      </Typography>

      <Box
        display="flex"
        flexDirection={isXs ? "column" : "row"}
        justifyContent="space-between"
        sx={{
          alignItems: isXs ? "center" : "flex-start",
          mb: isXs ? 0 : 2,
        }}
      >
        <ClientInfo />
        <CompanyInfo />
      </Box>

      <Box>
        <TextField
          size="small"
          label="Quote alias"
          name="alias"
          value={quoteSelection?.quote?.alias ?? ""}
          onChange={(event) =>
            dispatch(
              setQuote({
                alias: event.target.value,
              }),
            )
          }
          placeholder="Quote alias"
          fullWidth
          variant="outlined"
          sx={textFieldStyles}
        />

        <Box
          sx={{
            width: "fit-content",
            my: 2,
          }}
        >
          <AccessGate
            strategy={useBenefitStrategy(EBenefitTypes.E_SIGNATURE)}
            behavior={OverlayBehavior}
            behaviorConfig={{
              featureName: "E-Signature",
              tooltip: "Upgrade your plan to unlock this feature",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  name="requiredSign"
                  checked={Boolean(quoteSelection?.quote?.requiredSign)}
                  onChange={(event) =>
                    dispatch(
                      setQuote({
                        requiredSign: event.target.checked,
                      }),
                    )
                  }
                />
              }
              label="Required sign"
            />
          </AccessGate>
        </Box>
      </Box>

      <Box
        sx={{
          ...quoteModern.cardSx,
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              minWidth: 0,
            }}
          >
            <Box sx={quoteModern.iconBoxSx}>
              <WorkOutlineRounded
                sx={{
                  fontSize: 18,
                }}
              />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography sx={quoteModern.labelSx}>Services</Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: 15,
                  fontWeight: 800,
                  color: quoteModern.colors.textPrimary,
                  lineHeight: 1.2,
                }}
              >
                Services included in this quote
              </Typography>
            </Box>
          </Box>

          <Chip
            label={`${mode === QuoteModeEnum.BASIC ? basicQuoteList.length : advancedQuoteList.length} ${
              (mode === QuoteModeEnum.BASIC ? basicQuoteList.length : advancedQuoteList.length) === 1 ? "Service" : "Services"
            }`}
            size="small"
            sx={{
              bgcolor: quoteModern.colors.orange,
              color: "#fff",
              fontWeight: 800,
              flexShrink: 0,
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {mode === QuoteModeEnum.BASIC && <QuoteBasicList />}

          {mode === QuoteModeEnum.ADVANCED && <QuoteAdvancedList />}
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${colors.gray[200]}`,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            backgroundColor: colors.gray[50],
            px: 3,
            py: 2,
          }}
        >
          <Typography variant="h6" fontWeight={700} color={colors.gray[900]}>
            Quote Summary
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          <QuoteDeposit mode={mode} />

          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography color={colors.gray[600]}>Subtotal</Typography>

            <Typography fontWeight={600} fontSize={18}>
              ${total - (quoteSelection?.quote?.depositAmount ?? 0)}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box display="flex" justifyContent="space-between">
            <Typography variant="h6" fontWeight={700}>
              Total Amount
            </Typography>

            <Typography variant="h5" fontWeight={800} color={colors.orange.base}>
              ${total - (quoteSelection?.quote?.depositAmount ?? 0)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Box display="flex" justifyContent="center" marginBottom={3}>
        <Button
          fullWidth
          onClick={handleGenerateQuote}
          disabled={showLoading || !isValidPayload || isPreviewingAdvanced}
          variant="contained"
          sx={{
            py: 1.5,
            width: 200,
            borderRadius: 2,
            backgroundColor: colors.orange.base,
            textTransform: "none",
            fontWeight: 700,
            fontSize: 15,
            my: 2,

            ...(isPreviewingAdvanced && {
              backgroundColor: colors.gray[300],
              color: colors.gray[500],
              cursor: "not-allowed",
            }),

            "&:hover": {
              backgroundColor: colors.orange[600],
            },
          }}
        >
          {showLoading ? "Creating..." : isPreviewingAdvanced ? "Generate Quote (Preview Only)" : "Generate Quote"}
        </Button>
      </Box>
    </Box>
  );
};
